import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import WeatherWidget from "@/components/WeatherWidget";
import MyMissionsWidget from "@/components/dashboard/MyMissionsWidget";
import { Hero, KpiRow, AlertBlock, RestBlock, PrioritiesBlock, ActivityBlock, PerfTable, NewsBlock, CalendarBlock } from "@/components/dashboard/HomeBlocks";
import {
  Cloud, TrendingUp, TrendingDown, AlertTriangle, ArrowRight, ChevronRight,
  ShoppingCart, Percent, ChefHat, Trash2, Clipboard, Bell, Wrench, Sunrise,
  X, Sparkles, Calendar as CalIcon, Target, FileText, Store, PieChart,
  Trophy, Award, Loader2, Plus, MapPin,
} from "lucide-react";

const eur = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const pct = (v, d=1) => v == null ? "—" : `${v.toFixed(d)}%`;
const capName = (s) => (s || "").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const STATUS_COLORS = {
  conforme:      { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", label: "Conforme" },
  retard:        { dot: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50",   label: "En retard" },
  critique:      { dot: "bg-orange-600",  text: "text-orange-700",  bg: "bg-orange-50",  label: "Critique" },
  tres_critique: { dot: "bg-rose-600",    text: "text-rose-700",    bg: "bg-rose-50",    label: "Très critique" },
  inactif:       { dot: "bg-slate-300",   text: "text-slate-500",   bg: "bg-slate-50",   label: "Inactif" },
};

const PRIORITY_ICONS = {
  sunrise: Sunrise, chart: TrendingDown, trash: Trash2, chef: ChefHat,
  clipboard: Clipboard, alert: Bell, wrench: Wrench,
};

/* ─── Ranking mini-card ───────────────────────────────────────────── */
function RankingCard({ icon: Icon, label, subtitle, items, formatter, testid }) {
  const first = items?.[0];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 min-w-0 h-auto" data-testid={testid}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-100 grid place-items-center text-slate-600 shrink-0">
          <Icon className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-[10px] text-slate-400">{subtitle}</div>
        </div>
      </div>
      {first ? (
        <>
          <div className="flex items-baseline gap-2 mt-3">
            <div className="font-display font-black text-2xl text-slate-900">{formatter(first.value)}</div>
            <div className="text-[10px] font-bold text-blue-600">1er</div>
          </div>
          <div className="text-xs text-slate-600 truncate mt-0.5">{capName(first.name)}</div>
          {items.length > 1 && (
            <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
              {items.slice(1, 3).map(it => (
                <div key={it.rank} className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500 truncate flex-1">{it.rank}. {capName(it.name)}</span>
                  <span className="text-slate-700 font-semibold ml-2">{formatter(it.value)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="mt-3 text-xs text-slate-400 italic">Pas de données</div>
      )}
    </div>
  );
}

/* ─── Priority drawer ─────────────────────────────────────────────── */
function PriorityDrawer({ open, priority, onClose }) {
  if (!open || !priority) return null;
  return (
    <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} data-testid="priority-drawer">
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-display font-bold text-slate-900">{priority.label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{priority.count} élément{priority.count > 1 ? "s" : ""}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-5">
          <PriorityContent priority={priority}/>
        </div>
      </div>
    </div>
  );
}

function PriorityContent({ priority }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (priority.key === "rituels_late") {
      // Load Roy compliance stores (already available from a different endpoint)
      api.get("/roy/compliance/today").then(r => setList(r.data?.stores_late || []))
        .catch(() => setList([]));
    } else if (priority.href) setList([]);
    else setList([]);
  }, [priority.key]);
  if (priority.href) {
    return (
      <div>
        <div className="text-sm text-slate-600 mb-3">
          Ces éléments sont gérés sur une page dédiée.
        </div>
        <Link to={priority.href} className="inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:underline">
          Ouvrir <ChevronRight className="w-4 h-4"/>
        </Link>
      </div>
    );
  }
  if (priority.key === "rituels_late") {
    return (
      <div>
        <div className="text-xs text-slate-500 mb-3">Corners qui n'ont pas complété leurs 5 rituels aujourd'hui.</div>
        {list === null ? <div className="text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline"/></div> :
         list.length === 0 ? <div className="text-sm text-emerald-600 flex items-center gap-2"><Sparkles className="w-4 h-4"/>Tous les corners sont à jour ! 🎉</div> :
         <div className="space-y-1.5">
           {list.map((s, i) => (
             <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
               <span className="text-sm font-medium text-slate-900">{s.name || s.store_name}</span>
               <span className="text-xs text-rose-600 font-bold">{s.rituels_done || 0}/5</span>
             </div>
           ))}
         </div>
        }
      </div>
    );
  }
  return (
    <div className="text-sm text-slate-500 italic">
      Détail complet en cours d'implémentation.
    </div>
  );
}

/* ─── CA Alert bloc ────────────────────────────────────────────────── */
function CaAlertCard({ ca }) {
  if (!ca) return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-slate-400"/><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Alerte commerciale</div></div>
      <div className="text-xs text-slate-400">Aucune donnée.</div>
    </div>
  );
  const ecartPct = ca.ecart_pct;
  const isNeg = (ca.ecart || 0) < 0;
  const bg = isNeg ? "from-rose-50 to-rose-100 border-rose-200" : "from-emerald-50 to-emerald-100 border-emerald-200";
  const acc = isNeg ? "text-rose-700" : "text-emerald-700";
  return (
    <div className={`border rounded-2xl p-4 bg-gradient-to-br ${bg}`} data-testid="ca-alert-card">
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-4 h-4 ${acc}`}/>
        <div className={`text-[10px] font-bold uppercase tracking-wider ${acc}`}>Alerte commerciale</div>
      </div>
      <div className="text-sm font-bold text-slate-900 mt-1">
        {isNeg ? "Ventes en baisse marquée" : "Ventes en progression"}
      </div>
      <div className="text-[11px] text-slate-500">
        {new Date(ca.date).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "2-digit" })}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <div className="text-[9px] font-bold uppercase text-slate-500">Réalisé</div>
          <div className="text-sm font-black text-slate-900">{eur(ca.realise)}</div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase text-slate-500">Objectif</div>
          <div className="text-sm font-black text-slate-900">{eur(ca.objectif)}</div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase text-slate-500">Écart</div>
          <div className={`text-sm font-black ${acc}`}>{ecartPct != null ? (ecartPct > 0 ? "+" : "") + ecartPct.toFixed(1) + "%" : "—"}</div>
        </div>
      </div>
      {ca.lfl_pct != null && (
        <div className="mt-2 pt-2 border-t border-white/60 text-[10px] text-slate-600">
          LFL : <b className={ca.lfl_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>{ca.lfl_pct > 0 ? "+" : ""}{ca.lfl_pct.toFixed(1)}%</b>
        </div>
      )}
    </div>
  );
}

/* ─── Priorités bloc ─────────────────────────────────────────────── */
function PrioritiesCard({ priorities, onSelect }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="priorities-card">
      <div className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">Mes priorités du jour</div>
      <div className="space-y-1">
        {priorities.map(p => {
          const Icon = PRIORITY_ICONS[p.icon] || Bell;
          return (
            <button key={p.key} onClick={() => onSelect(p)}
                    data-testid={`priority-${p.key}`}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-left transition">
              <div className={`w-7 h-7 grid place-items-center rounded-lg bg-${p.color}-50 text-${p.color}-600 shrink-0`}>
                <Icon className="w-3.5 h-3.5"/>
              </div>
              <div className="flex-1 text-sm text-slate-800 truncate">{p.label}</div>
              <div className={`font-bold text-sm ${p.count > 0 ? "text-rose-600" : "text-slate-300"}`}>{p.count}</div>
              <ChevronRight className="w-4 h-4 text-slate-300"/>
            </button>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100">
        <Link to="/ops" className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
          Voir le détail de mes priorités <ArrowRight className="w-3 h-3"/>
        </Link>
      </div>
    </div>
  );
}

/* ─── Activité mini-card ─────────────────────────────────────────── */
function ActivityMini({ label, value, sub, icon: Icon, iconBg, iconColor, testid, subTone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 relative" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex items-end justify-between mt-2">
        <div className="font-display font-black text-2xl text-slate-900">{value}</div>
        {Icon && <div className={`w-9 h-9 rounded-xl ${iconBg} grid place-items-center ${iconColor}`}><Icon className="w-4 h-4"/></div>}
      </div>
      {sub && <div className={`text-[10px] mt-1 ${subTone || "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}

/* ─── Corners perf table ─────────────────────────────────────────── */
function CornersPerfTable({ rows }) {
  const nav = useNavigate();
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="corners-perf-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">Performance corners (J-1)</h3>
          <div className="text-[11px] text-slate-500">Triés par CA réalisé, statut coloré selon % d'atteinte</div>
        </div>
      </div>
      <div className="space-y-3 text-[11px] text-slate-500 mb-3">
        <div className="flex flex-wrap gap-3">
          {Object.entries(STATUS_COLORS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${v.dot}`}/>{v.label}
            </span>
          ))}
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="text-left py-2 font-bold">Corner</th>
              <th className="text-right py-2 font-bold">CA J-1</th>
              <th className="text-right py-2 font-bold">Objectif</th>
              <th className="text-right py-2 font-bold">Écart</th>
              <th className="text-right py-2 font-bold">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-slate-400 italic">Aucune donnée</td></tr>
            )}
            {rows.map(r => {
              const st = STATUS_COLORS[r.status] || STATUS_COLORS.inactif;
              return (
                <tr key={r.corner_key} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`corner-row-${r.corner_key}`}>
                  <td className="py-2 text-slate-900 font-medium">
                    <span className={`inline-block w-2 h-2 rounded-full ${st.dot} mr-2`}/>{capName(r.name)}
                  </td>
                  <td className="text-right py-2 font-bold">{eur(r.ca)}</td>
                  <td className="text-right py-2 text-slate-500">{eur(r.obj)}</td>
                  <td className={`text-right py-2 font-bold ${r.ecart >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.ecart >= 0 ? "+" : ""}{eur(r.ecart)}
                  </td>
                  <td className="text-right py-2">
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{r.pct}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 text-right">
        <button onClick={() => nav("/kpi/ca-jour")} className="text-xs font-bold text-blue-600 hover:underline">
          Voir en plein écran <ChevronRight className="w-3 h-3 inline"/>
        </button>
      </div>
    </div>
  );
}

/* ─── Activité récente feed ──────────────────────────────────────── */
function ActivityFeed({ items }) {
  const TYPE_META = {
    visite: { color: "bg-blue-500", label: "Visite", pill: "bg-blue-100 text-blue-700" },
    issue:  { color: "bg-orange-500", label: "Problématique", pill: "bg-orange-100 text-orange-700" },
    alerte: { color: "bg-rose-500", label: "Alerte", pill: "bg-rose-100 text-rose-700" },
    rituel: { color: "bg-emerald-500", label: "Rituel", pill: "bg-emerald-100 text-emerald-700" },
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">Activité récente</h3>
        <Link to="/journal" className="text-xs font-bold text-blue-600 hover:underline">Voir tout</Link>
      </div>
      {items.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">Aucune activité récente</div>}
      <div className="space-y-3 max-h-72 overflow-y-auto">
        {items.map((it, i) => {
          const meta = TYPE_META[it.type] || TYPE_META.visite;
          return (
            <div key={i} className="flex gap-2 items-start" data-testid={`feed-item-${i}`}>
              <div className={`w-2 h-2 rounded-full mt-2 ${meta.color} shrink-0`}/>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">{it.label}</div>
                <div className="text-[10px] text-slate-500">{it.user || "—"} · {timeAgo(it.at)}</div>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${meta.pill}`}>{meta.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 60000;
    if (diff < 1) return "à l'instant";
    if (diff < 60) return `il y a ${Math.round(diff)} min`;
    if (diff < 1440) return `il y a ${Math.round(diff/60)}h`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

/* ─── Calendar week bloc ─────────────────────────────────────────── */
function CalendarWeek() {
  const days = ["Lun", "Mar", "Mer", "Jeu", "Ven"];
  const today = new Date();
  const monday = new Date(today); monday.setDate(today.getDate() - today.getDay() + 1);
  const dates = days.map((_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  const isToday = (d) => d.toDateString() === today.toDateString();
  const nav = useNavigate();
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="calendar-week">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">Mon calendrier</h3>
        <button onClick={() => nav("/calendar")} className="text-xs font-bold text-blue-600 hover:underline">Voir le calendrier complet</button>
      </div>
      <div className="grid grid-cols-5 gap-1 mb-3">
        {dates.map((d, i) => (
          <div key={i} className={`p-2 rounded-lg text-center ${isToday(d) ? "bg-blue-600 text-white" : "bg-slate-50 text-slate-700"}`}>
            <div className="text-[9px] font-bold uppercase opacity-80">{days[i]}</div>
            <div className="font-display font-black text-lg">{d.getDate()}</div>
            <div className="flex justify-center gap-0.5 mt-1">
              {[0, 1, 2].map(j => <span key={j} className={`w-1 h-1 rounded-full ${isToday(d) ? "bg-white/70" : "bg-slate-300"}`}/>)}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 italic text-center py-2">Aucun événement synchronisé pour cette semaine</div>
    </div>
  );
}

/* ─── Actions rapides bar ────────────────────────────────────────── */
function QuickActionsBar() {
  const nav = useNavigate();
  const items = [
    { label: "Créer une action", icon: Sparkles, to: "/ops/interventions?new=mission", testid: "qa-mission" },
    { label: "Plan d'action", icon: Clipboard, to: "/plans", testid: "qa-plan" },
    { label: "Déclarer un rituel", icon: Award, to: "/journal?tab=messagerie", testid: "qa-rituel" },
    { label: "Visite magasin", icon: MapPin, to: "/ops/interventions?new=1", testid: "qa-visite" },
    { label: "Problématique", icon: AlertTriangle, to: "/issues", testid: "qa-issue" },
    { label: "Intervention", icon: Wrench, to: "/ops/interventions", testid: "qa-interv" },
    { label: "Document", icon: FileText, to: "/documents", testid: "qa-doc" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="quick-actions-bar">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">Actions rapides</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {items.map(it => (
          <button key={it.testid} onClick={() => nav(it.to)} data-testid={it.testid}
                  className="flex flex-col sm:flex-row items-center gap-2 p-2.5 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-xs font-semibold text-slate-700 transition">
            <it.icon className="w-4 h-4 text-blue-600"/>
            <span className="text-[10px] sm:text-xs text-center sm:text-left">{it.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Dashboard ─────────────────────────────────────────────── */
export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerPriority, setDrawerPriority] = useState(null);

  useEffect(() => {
    api.get("/dashboard/home")
      .then(r => setD(r.data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-400 text-center py-16">Chargement du tableau de bord…</div>;
  if (!d) return <div className="text-slate-400 text-center py-16">Impossible de charger les données</div>;

  const greetName = (user?.name || "").split(" ")[0] || "";

  return (
    <div className="dash-home -m-4 sm:-m-6 p-4 sm:p-6 min-h-full space-y-4" data-testid="dashboard-page" style={{ background: "var(--bg-app)" }}>
      <Hero userName={greetName}/>
      <KpiRow m={d.month_kpi} activity={d.activity}/>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch" data-testid="dash-middle">
        <AlertBlock ca={d.ca_alert}/>
        <RestBlock m={d.month_kpi}/>
        <WeatherWidget only="today" className="!rounded-lg !border-slate-100/60 shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_14px_rgba(15,23,42,.05)]"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dash-bottom-1">
        <PrioritiesBlock priorities={d.priorities} onSelect={setDrawerPriority}/>
        <div className="lg:col-span-2"><ActivityBlock a={d.activity}/></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dash-bottom-2">
        <PerfTable rows={d.corners_perf} date={d.period?.yesterday}/>
        <NewsBlock/>
        <CalendarBlock/>
      </div>

      {!(user?.is_corner) && <div className="[&>*]:!rounded-lg"><MyMissionsWidget/></div>}
      <div className="[&>*]:!rounded-lg"><QuickActionsBar/></div>

      <PriorityDrawer open={!!drawerPriority} priority={drawerPriority} onClose={() => setDrawerPriority(null)}/>
    </div>
  );
}
